/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect} from 'react'
import {Row, Col, Pagination, Form, Spinner} from 'react-bootstrap'
import {CoefIoLm} from '../core/_models'
import {Link} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import {deleteCoefIoLm} from '../core/_requests'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {showSwal} from '../../../../../ui-kit'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListCoefIoLmTableProps = {
  rows?: CoefIoLm[] | null
  isLoading: boolean
  onCoefIoLmDelete: (deletedId: number) => void
  subject: string
}

const confirmDialog = (title: string) => {
  return showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })
}

const ListCoefIoLmTable: FC<ListCoefIoLmTableProps> = ({
  rows,
  isLoading,
  onCoefIoLmDelete,
  subject,
}) => {
  const axiosPrivate = useAxiosPrivate()
  const [internalLoading, setInternalLoading] = useState(false)
  const [listCoefIoLm, setListCoefIoLm] = useState<CoefIoLm[]>([])

  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  const ability = useAbility(AbilityContext)

  const columnHelper = createColumnHelper<CoefIoLm>()

  const columns = [
    columnHelper.accessor('iso', {
      header: 'ISO',
      cell: (info) => (
        <span style={{fontWeight: '500'}}>{info.getValue() || '-'}</span>
      ),
      meta: {
        headerClass: 'text-start w-80px',
        cellClass: 'text-start w-80px',
      },
    }),
    columnHelper.accessor('input', {
      header: 'Input',
      cell: (info) => (
        <span
          title={info.getValue() ?? undefined}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {info.getValue() || '-'}
        </span>
      ),
      meta: {
        headerClass: 'text-start',
        cellClass: 'text-start',
      },
    }),
    columnHelper.accessor('output', {
      header: 'Output',
      cell: (info) => (
        <span
          title={info.getValue() ?? undefined}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {info.getValue() || '-'}
        </span>
      ),
      meta: {
        headerClass: 'text-start',
        cellClass: 'text-start',
      },
    }),
    (ability.can('update', subject) || ability.can('delete', subject)) &&
      columnHelper.accessor('id', {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const id = info.getValue()
          return (
            <div className='d-flex gap-3 justify-content-center'>
              {ability.can('update', subject) && id ? (
                <Link to={`/coef-io-lm/${id}`} className='link-primary'>
                  <span className='fa fa-eye'></span>
                </Link>
              ) : (
                ''
              )}
              {ability.can('delete', subject) && id ? (
                <Link to='#' className='link-danger' onClick={() => handleDelete(id)}>
                  <span className='fa fa-trash'></span>
                </Link>
              ) : (
                ''
              )}
            </div>
          )
        },
        meta: {
          headerClass: 'text-center w-100px',
          cellClass: 'text-center w-100px',
        },
        enableSorting: false,
      }),
  ].filter(Boolean) as ColumnDef<CoefIoLm, any>[]

  const handleDelete = (id: number) => {
    confirmDialog('Are you sure you want to delete this Coefficient Input Output LM record?').then((result) => {
      if (!result.isConfirmed) return

      setInternalLoading(true)
      deleteCoefIoLm(axiosPrivate, id)
        .then((res) => {
          setInternalLoading(false)
          if (res.status === 200) {
            showSwal({
              title: 'Coefficient Input Output LM record deleted',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              onCoefIoLmDelete(id)
            })
          }
        })
        .catch((e) => {
          setInternalLoading(false)
          console.error(e)
          showSwal('Error', 'Failed to delete Coefficient Input Output LM record.', 'error')
        })
    })
  }

  useEffect(() => {
    setListCoefIoLm([])
    setInternalLoading(true)
    return () => {
      setListCoefIoLm([])
      setInternalLoading(false)
    }
  }, [])

  useEffect(() => {
    setInternalLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (rows) {
      setListCoefIoLm(rows)
    }
    if (!isLoading) {
      setInternalLoading(false)
    }
  }, [rows, isLoading])

  const dataTable = useReactTable({
    data: listCoefIoLm || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    pageCount: Math.ceil((listCoefIoLm?.length || 0) / pagination.pageSize),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) range.push('...')
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) range.push('...')
      range.push(totalPages)
    }

    return range
  }

  const handleNavigationPageChange = (nav) => {
    setPagination({
      ...pagination,
      pageIndex: nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
    })
  }

  const handleDisplayPageChange = (e) => {
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()
  const firstIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1
  const lastIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
    dataTable.getRowModel().rows.length

  const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
    <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
          <div
            className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
            title={
              header.column.getCanSort()
                ? header.column.getNextSortingOrder() === 'asc'
                  ? 'Sort ascending'
                  : header.column.getNextSortingOrder() === 'desc'
                  ? 'Sort descending'
                  : 'Reset'
                : undefined
            }
            onClick={header.column.getToggleSortingHandler()}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.getCanSort() && (
              <span className='ms-2'>
                {{
                  asc: <i className='fa-solid fa-sort-up'></i>,
                  desc: <i className='fa-solid fa-sort-down'></i>,
                  false: <i className='fa-solid fa-sort'></i>,
                }[header.column.getIsSorted() as string] ?? <i className='fa-solid fa-sort'></i>}
              </span>
            )}
          </div>
        </th>
      ))}
    </tr>
  ))

  const displayBody =
    dataTable.getRowModel().rows.length > 0 ? (
      dataTable.getRowModel().rows.map((row) => (
        <tr key={row.id} className='align-middle'>
          {row.getVisibleCells().map((cell) => (
            <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      ))
    ) : (
      <tr className='align-middle'>
        <td colSpan={99} className='text-center'>
          No Data
        </td>
      </tr>
    )

  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {!internalLoading ? (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
              <i className='fa-solid fa-circle text-success fs-9 me-2'></i>
              Showing {dataTable.getRowModel().rows.length > 0 ? firstIndexPerPages : 0} to{' '}
              {dataTable.getRowModel().rows.length > 0 ? lastIndexPerPages : 0} of{' '}
              {listCoefIoLm?.length || 0} results
            </Col>
            <Col lg={3} md={3} className='action-table'>
              <Form.Group className='form-group' controlId='search-coef-io-lm'>
                <div className='input-with-icon'>
                  <Form.Control
                    type='text'
                    className='form-control-md'
                    onChange={(e) => {
                      setPagination({pageIndex: 0, pageSize: 10})
                      setGlobalFilter(e.target.value)
                    }}
                    placeholder='Search by ISO, Input or Output'
                    value={globalFilter}
                  />
                  <div className='input-icon'>
                    <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <table
            className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
            id='list_coef_io_lm'
          >
            <thead className='table-dark'>{displayHeader}</thead>
            <tbody className='gy-1 overflow-auto'>{displayBody}</tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Form.Select>
              list Coefficient Input Output LM
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => handleNavigationPageChange('prev')}
                  disabled={pagination.pageIndex === 0}
                />
                {pages.map((page, index) => {
                  if (page === '...') return <Pagination.Ellipsis key={index} />
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => setPagination({...pagination, pageIndex: page - 1})}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                <Pagination.Next
                  onClick={() => handleNavigationPageChange('next')}
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center' style={{height: '50vh'}}>
          <Spinner animation='border' role='status' variant='primary' className='me-3' />
          <div className='loading-text'>
            Loading
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </div>
        </div>
      )}
    </div>
  )
}

export {ListCoefIoLmTable}
